import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import customFetch from '@utils/customFetch';
import { useDashboardContext } from '@pages/DashboardLayout';
import { toast } from 'react-toastify';
import { SUBSCRIPTION_STATUS } from '@utils/constants';
import ConfirmModal from '@components/shared/ConfirmModal';
import SubscriptionCheckoutForm from '@components/dashboard/company/SubscriptionCheckoutForm';
import getStripePromise from '@utils/stripePromise';

const subscriptionQuery = (companyId) => ({
  queryKey: ['subscription', companyId],
  queryFn: async () => {
    const { data } = await customFetch.get(`/subscriptions/${companyId}`);
    return data;
  },
});

const discountQuery = (companyId) => ({
  queryKey: ['discount', companyId],
  queryFn: async () => {
    const { data } = await customFetch.get(`/companies/${companyId}/discount`);
    return data;
  },
});

export const loader = (queryClient) => async () => {
  const userData = await queryClient.ensureQueryData({
    queryKey: ['user'],
    queryFn: async () => {
      const { data } = await customFetch.get('/users/current-user');
      return data;
    },
  });
  const companyId = userData.user.company;
  if (!companyId) return {};
  await queryClient.ensureQueryData(subscriptionQuery(companyId));
  return { companyId };
};

const statusLabels = {
  [SUBSCRIPTION_STATUS.ACTIVE]: 'Aktiv',
  [SUBSCRIPTION_STATUS.PAST_DUE]: 'Überfällig',
  [SUBSCRIPTION_STATUS.CANCELED]: 'Gekündigt',
  [SUBSCRIPTION_STATUS.TRIALING]: 'Testphase',
  [SUBSCRIPTION_STATUS.UNPAID]: 'Unbezahlt',
  incomplete: 'Zahlung ausstehend',
  incomplete_expired: 'Zahlung abgelaufen',
};

const statusColors = {
  [SUBSCRIPTION_STATUS.ACTIVE]:
    'bg-green-50 text-green-700 ring-green-600/20',
  [SUBSCRIPTION_STATUS.PAST_DUE]:
    'bg-yellow-50 text-yellow-700 ring-yellow-600/20',
  [SUBSCRIPTION_STATUS.CANCELED]: 'bg-red-50 text-red-700 ring-red-600/20',
  [SUBSCRIPTION_STATUS.TRIALING]:
    'bg-blue-50 text-blue-700 ring-blue-600/20',
  [SUBSCRIPTION_STATUS.UNPAID]: 'bg-red-50 text-red-700 ring-red-600/20',
  incomplete: 'bg-yellow-50 text-yellow-700 ring-yellow-600/20',
  incomplete_expired: 'bg-red-50 text-red-700 ring-red-600/20',
};

const intervalLabels = { month: 'Monatlich', year: 'Jährlich' };
const intervalSuffix = { month: 'pro Monat', year: 'pro Jahr' };

const formatDate = (dateValue) => {
  if (!dateValue) return '–';
  const date = new Date(dateValue);
  if (isNaN(date.getTime())) return '–';
  return date.toLocaleDateString('de-CH');
};

// Beträge kommen wie überall in der Stripe-API in Rappen.
const formatAmount = (amount, currency = 'chf') =>
  `${currency.toUpperCase()} ${((amount || 0) / 100).toFixed(2)}`;

const rangeLabel = (from, to) => {
  if (to == null) return `ab ${from}`;
  if (from === to) return `${from}`;
  return `${from}–${to}`;
};

const TierTable = ({ tiers, currency }) => (
  <table className="min-w-full divide-y divide-gray-300">
    <thead>
      <tr>
        <th className="py-2 pl-0 pr-3 text-left text-sm font-semibold text-gray-900">
          Benutzer
        </th>
        <th className="py-2 pl-3 pr-0 text-right text-sm font-semibold text-gray-900">
          Preis pro Benutzer
        </th>
      </tr>
    </thead>
    <tbody className="divide-y divide-gray-200">
      {tiers.map((tier, idx) => {
        const from = idx === 0 ? 1 : (tiers[idx - 1].upTo ?? 0) + 1;
        return (
          <tr key={`${tier.upTo ?? 'inf'}-${idx}`}>
            <td className="whitespace-nowrap py-2 pl-0 pr-3 text-sm text-gray-900">
              {rangeLabel(from, tier.upTo)}
            </td>
            <td className="whitespace-nowrap py-2 pl-3 pr-0 text-right text-sm text-gray-900">
              {formatAmount(tier.unitAmount, currency)}
            </td>
          </tr>
        );
      })}
    </tbody>
  </table>
);

const BreakdownTable = ({ breakdown, currency, total, suffix }) => (
  <table className="min-w-full divide-y divide-gray-300">
    <thead>
      <tr>
        <th className="py-2 pl-0 pr-3 text-left text-sm font-semibold text-gray-900">
          Stufe
        </th>
        <th className="px-3 py-2 text-right text-sm font-semibold text-gray-900">
          Benutzer
        </th>
        <th className="px-3 py-2 text-right text-sm font-semibold text-gray-900">
          Preis
        </th>
        <th className="py-2 pl-3 pr-0 text-right text-sm font-semibold text-gray-900">
          Zwischensumme
        </th>
      </tr>
    </thead>
    <tbody className="divide-y divide-gray-200">
      {breakdown.map((row, idx) => (
        <tr key={`${row.from}-${idx}`}>
          <td className="whitespace-nowrap py-2 pl-0 pr-3 text-sm text-gray-500">
            {rangeLabel(row.from, row.to)}
          </td>
          <td className="whitespace-nowrap px-3 py-2 text-right text-sm text-gray-500">
            {row.quantity}
          </td>
          <td className="whitespace-nowrap px-3 py-2 text-right text-sm text-gray-500">
            {formatAmount(row.unitAmount, currency)}
          </td>
          <td className="whitespace-nowrap py-2 pl-3 pr-0 text-right text-sm text-gray-900">
            {formatAmount(row.subtotal, currency)}
          </td>
        </tr>
      ))}
    </tbody>
    <tfoot>
      <tr>
        <td
          colSpan={3}
          className="py-3 pl-0 pr-3 text-right text-sm font-semibold text-gray-900"
        >
          Total {suffix}
        </td>
        <td className="py-3 pl-3 pr-0 text-right text-sm font-semibold text-gray-900">
          {formatAmount(total, currency)}
        </td>
      </tr>
    </tfoot>
  </table>
);

const CompanySubscription = () => {
  const { user } = useDashboardContext();
  const companyId = user.company;
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery(subscriptionQuery(companyId));
  const subscription = data?.subscription;
  const companyStats = data?.companyStats;
  const pricing = data?.pricing;

  const { data: discountData } = useQuery({
    ...discountQuery(companyId),
    enabled: !!companyId,
  });
  const discount = discountData?.discount;

  const [actionLoading, setActionLoading] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [billingInterval, setBillingInterval] = useState('month');
  const [checkout, setCheckout] = useState(null);
  const [activating, setActivating] = useState(false);

  const options = pricing?.options || [];
  const monthlyOption = options.find((o) => o.interval === 'month');
  const yearlyOption = options.find((o) => o.interval === 'year');
  const selectedOption = options.find((o) => o.interval === billingInterval);

  // Ersparnis nicht hartkodieren, sondern aus den beiden Preisen ableiten —
  // sonst steht nach einer Preisänderung eine falsche Zahl auf der Seite.
  const yearlySaving = useMemo(() => {
    if (!monthlyOption?.total || !yearlyOption?.total) return null;
    const fullYear = monthlyOption.total * 12;
    const saved = fullYear - yearlyOption.total;
    if (saved <= 0) return null;
    const months = saved / monthlyOption.total;
    const roundedMonths = Math.round(months);
    return {
      percent: Math.round((saved / fullYear) * 100),
      months: Math.abs(months - roundedMonths) < 0.01 ? roundedMonths : null,
    };
  }, [monthlyOption, yearlyOption]);

  // Das Abo wird erst durch den Stripe-Webhook aktiv. Bis der eintrifft, so
  // lange nachfassen, statt sofort "aktiv" zu behaupten.
  const awaitActivation = useCallback(async () => {
    setActivating(true);
    setCheckout(null);
    queryClient.invalidateQueries(['user']);

    for (let attempt = 0; attempt < 10; attempt++) {
      const fresh = await queryClient.fetchQuery(subscriptionQuery(companyId));
      if (fresh?.subscription?.status === SUBSCRIPTION_STATUS.ACTIVE) {
        setActivating(false);
        toast.success('Abonnement ist aktiv.');
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, 2000));
    }

    setActivating(false);
    toast.info(
      'Zahlung erhalten. Die Aktivierung dauert einen Moment — lade die Seite gleich neu.'
    );
  }, [companyId, queryClient]);

  // Rückkehr aus einem Redirect-Payment (3DS, Twint): Stripe hängt
  // payment_intent_client_secret an die return_url.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const clientSecret = params.get('payment_intent_client_secret');
    if (!clientSecret) return;

    window.history.replaceState({}, '', window.location.pathname);

    getStripePromise()
      .then((stripe) => stripe.retrievePaymentIntent(clientSecret))
      .then(({ paymentIntent }) => {
        if (paymentIntent?.status === 'succeeded') {
          awaitActivation();
        } else if (paymentIntent?.status === 'processing') {
          toast.info('Deine Zahlung wird verarbeitet.');
        } else {
          toast.error('Die Zahlung war nicht erfolgreich. Bitte erneut versuchen.');
        }
      })
      .catch(() => {
        toast.error('Der Zahlungsstatus konnte nicht geprüft werden.');
      });
  }, [awaitActivation]);

  const handleStart = async () => {
    setActionLoading('start');
    try {
      const { data: result } = await customFetch.post(
        `/subscriptions/${companyId}`,
        { interval: billingInterval }
      );
      if (!result.clientSecret) {
        // Kann passieren, wenn die Rechnung bereits beglichen ist (z. B. durch
        // ein Guthaben) — dann fehlt nichts mehr ausser der Aktivierung.
        await awaitActivation();
        return;
      }
      setCheckout({
        clientSecret: result.clientSecret,
        amountDue: result.amountDue,
        currency: result.currency,
      });
    } catch (error) {
      toast.error(
        error?.response?.data?.msg || 'Das Abonnement konnte nicht gestartet werden'
      );
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancel = async () => {
    setActionLoading('cancel');
    try {
      const { data: result } = await customFetch.post(
        `/subscriptions/${companyId}/cancel`
      );
      queryClient.invalidateQueries(['subscription', companyId]);
      toast.success(result.msg);
    } catch (error) {
      toast.error(error?.response?.data?.msg || 'Fehler beim Kündigen');
    } finally {
      setActionLoading(null);
      setShowCancelModal(false);
    }
  };

  const handleReactivate = async () => {
    setActionLoading('reactivate');
    try {
      const { data: result } = await customFetch.post(
        `/subscriptions/${companyId}/reactivate`
      );
      queryClient.invalidateQueries(['subscription', companyId]);
      toast.success(result.msg);
    } catch (error) {
      toast.error(error?.response?.data?.msg || 'Fehler beim Reaktivieren');
    } finally {
      setActionLoading(null);
    }
  };

  const handlePortal = async () => {
    setActionLoading('portal');
    try {
      const { data: result } = await customFetch.post(
        `/subscriptions/${companyId}/portal`
      );
      window.location.href = result.url;
    } catch (error) {
      toast.error(
        error?.response?.data?.msg || 'Fehler beim Öffnen des Kundenportals'
      );
      setActionLoading(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-svisit-primary-600" />
      </div>
    );
  }

  const paymentPending = subscription?.status === 'incomplete';

  const checkoutPanel = checkout && (
    <div className="bg-white shadow-xs ring-1 ring-gray-900/5 sm:rounded-lg">
      <div className="px-4 py-6 sm:p-8">
        <h2 className="text-base font-semibold leading-7 text-gray-900">
          Zahlung
        </h2>
        <p className="mb-6 mt-1 text-sm text-gray-500">
          Fällig jetzt:{' '}
          <span className="font-medium text-gray-900">
            {formatAmount(checkout.amountDue, checkout.currency)}
          </span>
        </p>
        <SubscriptionCheckoutForm
          clientSecret={checkout.clientSecret}
          amountLabel={formatAmount(checkout.amountDue, checkout.currency)}
          onPaid={awaitActivation}
        />
        <button
          type="button"
          onClick={() => setCheckout(null)}
          className="mt-4 text-sm text-gray-500 hover:text-gray-700"
        >
          Abbrechen
        </button>
      </div>
    </div>
  );

  const activationNotice = activating && (
    <div className="rounded-lg bg-blue-50 p-4 text-sm text-blue-800 ring-1 ring-inset ring-blue-600/20">
      Zahlung erhalten — das Abonnement wird aktiviert…
    </div>
  );

  return (
    <div className="bg-white px-6 py-6 sm:py-6 lg:px-8">
      <ConfirmModal
        open={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={handleCancel}
        title="Abonnement kündigen"
        message="Möchtest du dein Abonnement wirklich zum Ende der aktuellen Periode kündigen?"
        confirmText="Kündigen"
        loading={actionLoading === 'cancel'}
        loadingText="Wird gekündigt..."
      />

      <div className="mb-8">
        <h1 className="text-base font-semibold leading-6 text-gray-900">
          Abonnement
        </h1>
        <p className="mt-2 text-sm text-gray-700">
          Verwalte dein Firmen-Abonnement und Zahlungsinformationen.
        </p>
      </div>

      {!subscription ? (
        <div className="space-y-6">
          {activationNotice}
          {checkoutPanel}

          {!checkout && !activating && (
            <div className="bg-white shadow-xs ring-1 ring-gray-900/5 sm:rounded-lg">
              <div className="px-4 py-6 sm:p-8">
                <h2 className="text-base font-semibold leading-7 text-gray-900">
                  sVisit Business
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  Ein gestaffelter Preis pro Benutzer — je grösser das Team,
                  desto günstiger der einzelne Platz.
                </p>

                {/* Vorab zugesagter Rabatt: die Beträge unten sind der
                    Listenpreis, abgezogen wird er auf der Rechnung. */}
                {discount?.pending && (
                  <div className="mt-4 rounded-md bg-green-50 p-3 ring-1 ring-inset ring-green-600/20">
                    <p className="text-sm text-green-800">
                      Für euch hinterlegt:{' '}
                      <span className="font-medium">
                        {discount.percentOff
                          ? `${discount.percentOff}% Rabatt`
                          : `CHF ${discount.amountOff?.toFixed(2)} Rabatt`}
                      </span>{' '}
                      {discount.duration === 'forever' && 'auf jede Rechnung'}
                      {discount.duration === 'once' && 'auf die erste Rechnung'}
                      {discount.duration === 'repeating' &&
                        `für ${discount.durationInMonths} Monate`}
                      . Die Preise unten sind ohne Rabatt gerechnet — abgezogen
                      wird er ab der ersten Rechnung.
                    </p>
                  </div>
                )}

                {!selectedOption ? (
                  <p className="mt-6 text-sm text-gray-500">
                    Die Preise konnten nicht geladen werden. Bitte lade die
                    Seite neu oder kontaktiere den Support.
                  </p>
                ) : (
                  <>
                    <div className="mt-6 inline-flex rounded-md bg-gray-100 p-1">
                      {options.map((option) => (
                        <button
                          key={option.interval}
                          type="button"
                          onClick={() => setBillingInterval(option.interval)}
                          className={`rounded px-4 py-2 text-sm font-medium ${
                            billingInterval === option.interval
                              ? 'bg-white text-gray-900 shadow-xs'
                              : 'text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          {intervalLabels[option.interval] || option.interval}
                          {option.interval === 'year' && yearlySaving && (
                            <span className="ml-2 rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                              {yearlySaving.months
                                ? `${yearlySaving.months} Monate geschenkt`
                                : `${yearlySaving.percent}% sparen`}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>

                    <div className="mt-6 overflow-x-auto">
                      <TierTable
                        tiers={selectedOption.tiers}
                        currency={selectedOption.currency}
                      />
                    </div>

                    <dl className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2">
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Aktuelle Benutzer
                        </dt>
                        <dd className="mt-1 text-2xl font-semibold text-gray-900">
                          {pricing?.currentUsers ?? 0}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Kosten {intervalSuffix[billingInterval]}
                        </dt>
                        <dd className="mt-1 text-2xl font-semibold text-gray-900">
                          {formatAmount(
                            selectedOption.total,
                            selectedOption.currency
                          )}
                        </dd>
                      </div>
                    </dl>

                    <div className="mt-6 overflow-x-auto">
                      <BreakdownTable
                        breakdown={selectedOption.breakdown}
                        currency={selectedOption.currency}
                        total={selectedOption.total}
                        suffix={intervalSuffix[billingInterval]}
                      />
                    </div>

                    <button
                      type="button"
                      onClick={handleStart}
                      disabled={actionLoading === 'start'}
                      className="mt-8 rounded-md bg-svisit-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-xs hover:bg-svisit-primary-500 disabled:cursor-not-allowed disabled:bg-gray-300"
                    >
                      {actionLoading === 'start'
                        ? 'Wird vorbereitet...'
                        : 'Abo starten'}
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {activationNotice}

          {/* Status & Overview */}
          <div className="bg-white shadow-xs ring-1 ring-gray-900/5 sm:rounded-lg">
            <div className="px-4 py-6 sm:p-8">
              <h2 className="text-base font-semibold leading-7 text-gray-900 mb-6">
                Abo-Übersicht
              </h2>
              <dl className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Status</dt>
                  <dd className="mt-1">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ring-1 ring-inset ${
                        statusColors[subscription.status] ||
                        'bg-gray-50 text-gray-700 ring-gray-600/20'
                      }`}
                    >
                      {statusLabels[subscription.status] ||
                        subscription.status}
                    </span>
                    {subscription.cancelAtPeriodEnd && (
                      <span className="ml-2 text-xs text-red-600">
                        Wird gekündigt
                      </span>
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    Aktuelle Periode
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formatDate(subscription.currentPeriodStart)}
                    {' '}&ndash;{' '}
                    {formatDate(subscription.currentPeriodEnd)}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    Letzte Rechnung
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {subscription.latestInvoice
                      ? formatAmount(subscription.latestInvoice.amountPaid)
                      : '–'}
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          {paymentPending && !checkout && (
            <div className="rounded-lg bg-yellow-50 p-4 ring-1 ring-inset ring-yellow-600/20">
              <p className="text-sm text-yellow-800">
                Die Erstzahlung ist noch offen. Das Abonnement wird erst danach
                aktiv.
              </p>
              <button
                type="button"
                onClick={handleStart}
                disabled={actionLoading === 'start'}
                className="mt-3 rounded-md bg-svisit-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-xs hover:bg-svisit-primary-500 disabled:cursor-not-allowed disabled:bg-gray-300"
              >
                {actionLoading === 'start'
                  ? 'Wird vorbereitet...'
                  : 'Zahlung abschliessen'}
              </button>
            </div>
          )}

          {checkoutPanel}

          {/* Users */}
          {companyStats && (
            <div className="bg-white shadow-xs ring-1 ring-gray-900/5 sm:rounded-lg">
              <div className="px-4 py-6 sm:p-8">
                <h2 className="text-base font-semibold leading-7 text-gray-900 mb-6">
                  Benutzer
                </h2>
                <dl className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">
                      Aktuelle Benutzer
                    </dt>
                    <dd className="mt-1 text-2xl font-semibold text-gray-900">
                      {companyStats.currentUsers}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">
                      Bezahlte Benutzer
                    </dt>
                    <dd className="mt-1 text-2xl font-semibold text-gray-900">
                      {companyStats.paidUsers}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          )}

          {/* Preisdetails: Staffelung auf die gebuchte Menge */}
          {subscription.billing?.breakdown?.length > 0 && (
            <div className="bg-white shadow-xs ring-1 ring-gray-900/5 sm:rounded-lg">
              <div className="px-4 py-6 sm:p-8">
                <h2 className="text-base font-semibold leading-7 text-gray-900">
                  Preisdetails
                </h2>
                <p className="mb-6 mt-1 text-sm text-gray-500">
                  {intervalLabels[subscription.billing.interval] ||
                    subscription.billing.interval}{' '}
                  abgerechnet, gestaffelt über {subscription.billing.quantity}{' '}
                  Benutzer.
                </p>
                <div className="overflow-x-auto">
                  <BreakdownTable
                    breakdown={subscription.billing.breakdown}
                    currency={subscription.billing.currency}
                    total={subscription.billing.total}
                    suffix={intervalSuffix[subscription.billing.interval] || ''}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Discount */}
          {discount && (
            <div className="bg-white shadow-xs ring-1 ring-gray-900/5 sm:rounded-lg">
              <div className="px-4 py-6 sm:p-8">
                <h2 className="text-base font-semibold leading-7 text-gray-900 mb-6">
                  Aktiver Rabatt
                </h2>
                <dl className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Rabatt</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {discount.percentOff
                        ? `${discount.percentOff}% auf jede Rechnung`
                        : `CHF ${discount.amountOff?.toFixed(2)} pro Rechnung`}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Dauer</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {discount.duration === 'forever' && 'Permanent'}
                      {discount.duration === 'once' && 'Einmalig, wird auf nächster Rechnung angewendet'}
                      {discount.duration === 'repeating' &&
                        `${discount.durationInMonths} Monate`}
                    </dd>
                  </div>
                  {discount.name && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Bezeichnung</dt>
                      <dd className="mt-1 text-sm text-gray-900">{discount.name}</dd>
                    </div>
                  )}
                </dl>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={handlePortal}
              disabled={actionLoading === 'portal'}
              className="rounded-md bg-svisit-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-xs hover:bg-svisit-primary-500 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {actionLoading === 'portal'
                ? 'Wird geöffnet...'
                : 'Zahlungsmethoden verwalten'}
            </button>

            {subscription.cancelAtPeriodEnd ? (
              <button
                onClick={handleReactivate}
                disabled={actionLoading === 'reactivate'}
                className="rounded-md bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-xs hover:bg-green-500 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {actionLoading === 'reactivate'
                  ? 'Wird reaktiviert...'
                  : 'Abonnement reaktivieren'}
              </button>
            ) : (
              subscription.status !== SUBSCRIPTION_STATUS.CANCELED && (
                <button
                  onClick={() => setShowCancelModal(true)}
                  disabled={actionLoading === 'cancel'}
                  className="rounded-md bg-white px-4 py-2 text-sm font-semibold text-red-600 shadow-xs ring-1 ring-inset ring-red-300 hover:bg-red-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  Abonnement kündigen
                </button>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanySubscription;
